import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { commentApi } from '../api/comment';
import { useUserStore } from '../stores/userStore';
import { useNavigateWithScrollMemory } from './useNavigateWithScrollMemory';
import { saveCurrentPathAndRedirectToLoginWithScroll } from '../utils/authUtils';
import { trackContentLike } from '../lib/gaEvents';
import { CONTENT_TYPE } from '../constants/gaEnums';
import { getPageTypeFromPath } from '../lib/gaParams';

const useCommentActions = ({ comment, onEdit, onReply, replyTargetId }) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const { pathname } = useLocation();
  const user = useUserStore((state) => state.user);
  const pageType = getPageTypeFromPath(pathname);
  const isAdmin = useUserStore((state) => state.isAdmin);

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isLiked, setIsLiked] = useState(comment.isLiked);
  const [likeCount, setLikeCount] = useState(comment.likeCount || 0);
  const [isCancelEditDialogOpen, setIsCancelEditDialogOpen] = useState(false);
  const [isCancelReplyDialogOpen, setIsCancelReplyDialogOpen] = useState(false);

  const isCommentAuthor = user && comment.user && user.id === comment.user.id;

  // Edit handlers
  const handleEditClick = () => {
    setIsEditing(true);
    setEditContent(comment.content);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editContent.trim()) return;

    try {
      await onEdit(comment.id, editContent);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to edit comment:', error);
    }
  };

  const handleEditCancel = () => {
    if (editContent.trim() !== comment.content.trim()) {
      setIsCancelEditDialogOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleEditCancelConfirm = () => {
    setIsEditing(false);
    setIsCancelEditDialogOpen(false);
  };

  const handleEditCancelCancel = () => {
    setIsCancelEditDialogOpen(false);
  };

  // Reply handlers
  const handleCancelReply = () => {
    setReplyContent('');
    setIsReplying(false);
    setIsCancelReplyDialogOpen(false);
  };

  const handleReplyIconClick = () => {
    if (!user || Object.keys(user).length === 0) {
      saveCurrentPathAndRedirectToLoginWithScroll(navigateWithScrollMemory);
      return;
    }
    setIsReplying(!isReplying);
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    try {
      await onReply(replyTargetId, replyContent, comment.user.id);
      setReplyContent('');
      setIsReplying(false);
    } catch (error) {
      console.error('Failed to submit reply:', error);
    }
  };

  const handleCancelReplyClick = () => {
    if (replyContent.trim().length === 0) {
      handleCancelReply();
    } else {
      setIsCancelReplyDialogOpen(true);
    }
  };

  const handleCancelReplyCancel = () => {
    setIsCancelReplyDialogOpen(false);
  };

  const handleCancelReplyConfirm = () => {
    handleCancelReply();
  };

  // Like handler
  const handleLikeToggle = async () => {
    if (!user || Object.keys(user).length === 0) {
      saveCurrentPathAndRedirectToLoginWithScroll(navigateWithScrollMemory);
      return;
    }

    try {
      if (isLiked) {
        await commentApi.deleteLike(comment.id);
        setLikeCount(likeCount - 1);
      } else {
        await commentApi.addLike(comment.id);
        setLikeCount(likeCount + 1);
        trackContentLike(
          { contentType: CONTENT_TYPE.COMMENT, contentId: comment.id, pageType },
          user,
        );
      }
      setIsLiked(!isLiked);
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  return {
    // State
    isEditing,
    editContent,
    setEditContent,
    isReplying,
    replyContent,
    setReplyContent,
    isLiked,
    likeCount,
    isCancelEditDialogOpen,
    isCancelReplyDialogOpen,
    // Computed
    isCommentAuthor,
    isAdmin,
    // Navigation
    navigateWithScrollMemory,
    // Edit handlers
    handleEditClick,
    handleEditSubmit,
    handleEditCancel,
    handleEditCancelConfirm,
    handleEditCancelCancel,
    // Reply handlers
    handleReplyIconClick,
    handleReplySubmit,
    handleCancelReply,
    handleCancelReplyClick,
    handleCancelReplyCancel,
    handleCancelReplyConfirm,
    // Like handler
    handleLikeToggle,
  };
};

export default useCommentActions;
